import type { MessageKey } from '../../i18n';

// ═══════════════════════════════════════════════════════════════════
// FAGOMRÅDER — the high-level view of a role (owner decisions 1A/2A/3A,
// 2026-10-19).
//
// The owner: «matrisen skal bare vise highlevel hva slags rolle de innehar»,
// with the detail one click away, and without sideways scrolling as the
// roles grow. So /sa draws ROLES AS ROWS against a FIXED set of areas, and
// each cell names the LEVEL the role holds there — «Administrator» on
// booking, «Lesetilgang» on economy.
//
// ── A LEVEL IS A NAME FOR A SET OF CAPABILITIES, NOTHING MORE (1A) ──
//
// No table, no new authority. A role is still exactly its capability rows;
// this module only recognises which named set a role's capabilities in one
// area match. A set that matches no level reads «Tilpasset», and choosing a
// level REPLACES that area's capabilities with the level's set — the same
// write the detailed grid makes, through the same endpoint, audited the same
// way. Nobody gains or loses anything by this view existing.
//
// The levels were fitted to the system roles that exist (packages/db/src/
// rbac.ts) so each lands on a name rather than on «Tilpasset»: Resepsjon is
// «Bruker» on booking, Anlegg is «Drift» on courts, Daglig leder is «Daglig
// drift» on booking. `role-areas.test.ts` pins that fit, so a seed change
// that breaks it fails a test instead of quietly turning a column custom.
//
// `'all'` means every capability of the area the tab offers — so a capability
// added to an area later belongs to its top level the day it exists, and a
// role that held «everything» before it then reads «Tilpasset» until somebody
// decides. That is the honest answer: it no longer holds everything.
// ═══════════════════════════════════════════════════════════════════

export interface AreaLevel {
  key: string;
  label: MessageKey;
  caps: readonly string[] | 'all';
}

export interface Area {
  key: string;
  label: MessageKey;
  /** The capability domains (the part before the first dot) that belong here. */
  domains: readonly string[];
  /** Lowest first. */
  levels: readonly AreaLevel[];
}

export const AREAS: readonly Area[] = [
  {
    key: 'organization',
    label: 'saAreas.area.organization',
    domains: ['organization', 'bug'],
    levels: [
      { key: 'read', label: 'saAreas.level.read', caps: ['organization.read'] },
      {
        key: 'daily',
        label: 'saAreas.level.daily',
        caps: ['organization.read', 'organization.write', 'organization.rename', 'bug.manage'],
      },
      {
        key: 'admin',
        label: 'saAreas.level.admin',
        caps: [
          'organization.read',
          'organization.write',
          'organization.rename',
          'bug.manage',
          'organization.access.manage',
          // Reading the organization's own log (#935, 2026-11-01) is derived
          // from handing out roles, so it sits in the same level.
          'organization.audit.read',
        ],
      },
      { key: 'owner', label: 'saAreas.level.owner', caps: 'all' },
    ],
  },
  {
    key: 'court',
    label: 'saAreas.area.court',
    // Equipment beside the courts (`asset.manage`, #912) is venue setup, so
    // it is read with the courts; it sits in «Drift» because it was derived
    // from `court.manage`, which that level has.
    domains: ['court', 'asset'],
    levels: [
      {
        key: 'user',
        label: 'saAreas.level.user',
        caps: ['court.set_out_of_service', 'court.screen.control'],
      },
      {
        key: 'operate',
        label: 'saAreas.level.operate',
        caps: [
          'court.manage',
          'asset.manage',
          'court.set_out_of_service',
          'court.screen.control',
          'court.pair_device',
        ],
      },
      { key: 'admin', label: 'saAreas.level.admin', caps: 'all' },
    ],
  },
  {
    key: 'booking',
    label: 'saAreas.area.booking',
    domains: ['booking'],
    levels: [
      { key: 'rules', label: 'saAreas.level.rules', caps: ['booking.rules.manage'] },
      // `booking.override_constraints` — the `adminOverride` flag — sits in
      // both operating levels since it came into force (#949, 2026-10-31):
      // everyone who could book could already send it, so the levels say so.
      // A deployment that narrows it in /sa reads «Tilpasset» there, honestly.
      {
        key: 'user',
        label: 'saAreas.level.user',
        caps: [
          'booking.create',
          'booking.modify',
          'booking.delete',
          'booking.override_constraints',
        ],
      },
      {
        key: 'daily',
        label: 'saAreas.level.daily',
        caps: [
          'booking.create',
          'booking.modify',
          'booking.delete',
          'booking.override_constraints',
          'booking.rules.manage',
        ],
      },
      { key: 'admin', label: 'saAreas.level.admin', caps: 'all' },
    ],
  },
  {
    key: 'activity',
    label: 'saAreas.area.activity',
    domains: ['activity', 'planned_match'],
    levels: [{ key: 'admin', label: 'saAreas.level.admin', caps: 'all' }],
  },
  {
    key: 'tournament',
    label: 'saAreas.area.tournament',
    domains: ['tournament', 'result', 'rating'],
    levels: [
      { key: 'results', label: 'saAreas.level.results', caps: ['result.record'] },
      {
        key: 'lead',
        label: 'saAreas.level.lead',
        caps: ['tournament.manage', 'result.record', 'result.correct', 'rating.commit'],
      },
      { key: 'admin', label: 'saAreas.level.admin', caps: 'all' },
    ],
  },
  {
    key: 'member',
    label: 'saAreas.area.member',
    domains: ['member', 'membership'],
    levels: [
      {
        key: 'user',
        label: 'saAreas.level.user',
        caps: ['member.manage', 'membership.approve'],
      },
      { key: 'admin', label: 'saAreas.level.admin', caps: 'all' },
    ],
  },
  {
    key: 'messaging',
    label: 'saAreas.area.messaging',
    domains: ['messaging'],
    levels: [
      { key: 'user', label: 'saAreas.level.user', caps: ['messaging.send_as_organization'] },
      {
        key: 'lead',
        label: 'saAreas.level.responsible',
        caps: ['messaging.send_as_organization', 'messaging.broadcast', 'messaging.moderate'],
      },
      { key: 'admin', label: 'saAreas.level.admin', caps: 'all' },
    ],
  },
  {
    key: 'finance',
    label: 'saAreas.area.finance',
    domains: ['finance', 'payment'],
    levels: [
      { key: 'read', label: 'saAreas.level.read', caps: ['finance.read'] },
      {
        key: 'admin',
        label: 'saAreas.level.admin',
        caps: ['finance.read', 'payment.refund', 'payment.manual_action'],
      },
      { key: 'owner', label: 'saAreas.level.owner', caps: 'all' },
    ],
  },
];

/**
 * Anything the areas above do not name still gets a column, so a capability
 * in a new domain is never invisible here. One level: all of it.
 */
export const OTHER_AREA: Area = {
  key: 'other',
  label: 'saAreas.area.other',
  domains: [],
  levels: [{ key: 'admin', label: 'saAreas.level.admin', caps: 'all' }],
};

const domainOf = (capability: string) => capability.split('.')[0];

export function areaOf(capability: string): Area {
  const domain = domainOf(capability);
  return AREAS.find((a) => a.domains.includes(domain)) ?? OTHER_AREA;
}

/** The areas this tab has capabilities in, in the product's order, each with its capabilities. */
export function areasFor(available: readonly string[]): { area: Area; caps: string[] }[] {
  const out: { area: Area; caps: string[] }[] = [];
  for (const area of [...AREAS, OTHER_AREA]) {
    const caps = available.filter((c) => areaOf(c) === area);
    if (caps.length > 0) out.push({ area, caps });
  }
  return out;
}

/** A level's capabilities, limited to what this tab offers. */
export function levelCaps(level: AreaLevel, areaCaps: readonly string[]): string[] {
  if (level.caps === 'all') return [...areaCaps];
  const offered = new Set(areaCaps);
  return level.caps.filter((c) => offered.has(c));
}

export type AreaStanding =
  | { kind: 'none' }
  | { kind: 'level'; level: AreaLevel }
  | { kind: 'custom'; held: number; total: number };

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/** What a role holds in one area, as a name. */
export function standingIn(
  area: Area,
  areaCaps: readonly string[],
  roleCaps: readonly string[],
): AreaStanding {
  const held = areaCaps.filter((c) => roleCaps.includes(c));
  if (held.length === 0) return { kind: 'none' };
  // LOWEST FIRST. When the tab offers fewer capabilities, two levels can
  // collapse to one set — «Eier» on economy is then just finance.read — and
  // the modest name is the true one: the role holds no more than the lowest.
  for (const level of area.levels) {
    const want = levelCaps(level, areaCaps);
    if (want.length > 0 && sameSet(held, want)) return { kind: 'level', level };
  }
  return { kind: 'custom', held: held.length, total: areaCaps.length };
}

/**
 * The role's capabilities after choosing a level in one area: the area's
 * capabilities are replaced, everything outside the area is kept. `null`
 * means «Ingen» — none of the area.
 */
export function withLevel(
  roleCaps: readonly string[],
  areaCaps: readonly string[],
  level: AreaLevel | null,
): string[] {
  const inArea = new Set(areaCaps);
  const kept = roleCaps.filter((c) => !inArea.has(c));
  return [...kept, ...(level ? levelCaps(level, areaCaps) : [])];
}

// ── COLOUR AND STRENGTH (owner decision 2A, 2026-10-20) ────────────────
//
// «Vi må ha bedre grafikk, litt farger»: each area has its own hue, and the
// cell gets stronger the higher the level, with dots saying the same thing in
// a form that does not depend on telling colours apart. Full class strings,
// never assembled, so Tailwind's scan finds every one.

export interface AreaTone {
  /** Heading text and icon. */
  head: string;
  /** Cell fill and text, weakest to strongest (one, two, three dots). */
  cell: readonly [string, string, string];
  /** «Tilpasset»: an outline in the area's hue, so it reads as «not a level». */
  custom: string;
  /** The open panel's band. */
  panel: string;
}

const TONE: Record<string, AreaTone> = {
  organization: {
    head: 'text-violet-800',
    cell: [
      'bg-violet-50 text-violet-900',
      'bg-violet-100 text-violet-900',
      'bg-violet-200 text-violet-950',
    ],
    custom: 'border border-dashed border-violet-400 text-violet-900',
    panel: 'bg-violet-50 border-violet-300',
  },
  court: {
    head: 'text-sky-800',
    cell: ['bg-sky-50 text-sky-900', 'bg-sky-100 text-sky-900', 'bg-sky-200 text-sky-950'],
    custom: 'border border-dashed border-sky-400 text-sky-900',
    panel: 'bg-sky-50 border-sky-300',
  },
  booking: {
    head: 'text-emerald-800',
    cell: [
      'bg-emerald-50 text-emerald-900',
      'bg-emerald-100 text-emerald-900',
      'bg-emerald-200 text-emerald-950',
    ],
    custom: 'border border-dashed border-emerald-400 text-emerald-900',
    panel: 'bg-emerald-50 border-emerald-300',
  },
  activity: {
    head: 'text-orange-800',
    cell: [
      'bg-orange-50 text-orange-900',
      'bg-orange-100 text-orange-900',
      'bg-orange-200 text-orange-950',
    ],
    custom: 'border border-dashed border-orange-400 text-orange-900',
    panel: 'bg-orange-50 border-orange-300',
  },
  tournament: {
    head: 'text-amber-800',
    cell: [
      'bg-amber-50 text-amber-900',
      'bg-amber-100 text-amber-900',
      'bg-amber-200 text-amber-950',
    ],
    custom: 'border border-dashed border-amber-400 text-amber-900',
    panel: 'bg-amber-50 border-amber-300',
  },
  member: {
    head: 'text-teal-800',
    cell: ['bg-teal-50 text-teal-900', 'bg-teal-100 text-teal-900', 'bg-teal-200 text-teal-950'],
    custom: 'border border-dashed border-teal-400 text-teal-900',
    panel: 'bg-teal-50 border-teal-300',
  },
  messaging: {
    head: 'text-fuchsia-800',
    cell: [
      'bg-fuchsia-50 text-fuchsia-900',
      'bg-fuchsia-100 text-fuchsia-900',
      'bg-fuchsia-200 text-fuchsia-950',
    ],
    custom: 'border border-dashed border-fuchsia-400 text-fuchsia-900',
    panel: 'bg-fuchsia-50 border-fuchsia-300',
  },
  finance: {
    head: 'text-lime-800',
    cell: ['bg-lime-50 text-lime-900', 'bg-lime-100 text-lime-900', 'bg-lime-200 text-lime-950'],
    custom: 'border border-dashed border-lime-500 text-lime-900',
    panel: 'bg-lime-50 border-lime-300',
  },
  other: {
    head: 'text-slate-700',
    cell: [
      'bg-slate-50 text-slate-900',
      'bg-slate-100 text-slate-900',
      'bg-slate-200 text-slate-950',
    ],
    custom: 'border border-dashed border-slate-400 text-slate-900',
    panel: 'bg-slate-50 border-slate-300',
  },
};

export function toneOf(area: Area): AreaTone {
  return TONE[area.key] ?? TONE.other;
}

/**
 * How strong a level is, as one to three dots: its place among the area's
 * levels, scaled so every area's top level is three dots — «Administrator»
 * on activities (the only level there) reads as strong as on booking.
 */
export function levelStrength(area: Area, level: AreaLevel): 1 | 2 | 3 {
  const i = area.levels.findIndex((l) => l.key === level.key);
  const rank = (i + 1) / area.levels.length;
  return rank > 2 / 3 ? 3 : rank > 1 / 3 ? 2 : 1;
}

/** Which group a role is listed under on the one-page overview (1A). */
export type RoleGroup = 'both' | 'centre' | 'club';
export function groupOf(appliesTo: string | null): RoleGroup {
  return appliesTo === 'CENTRE' ? 'centre' : appliesTo === 'CLUB' ? 'club' : 'both';
}
